import {
	type FeaturesDefinition,
	type FeaturesDefinitionBase,
	type CompiledFeaturesDefinition,
	type ExperimentalFeaturesProp,
	type ResolvedFeatures,
	type SingleFeatureDescriptor,
} from './types';

export const featuresResolver = <FeatureDefs extends FeaturesDefinitionBase>(
	definitions: FeaturesDefinition<FeatureDefs>,
) => {
	let prevFeaturesProp: ExperimentalFeaturesProp<FeatureDefs> | null = null;
	let prevResult: ResolvedFeatures<FeatureDefs> | null = null;
	let compiledDefinitions: CompiledFeaturesDefinition<FeatureDefs> | null = null;

	return (featuresProp: ExperimentalFeaturesProp<FeatureDefs> = {}) => {
		const changedResult = {} as ResolvedFeatures<FeatureDefs>;
		let changed = false;
		compiledDefinitions = compiledDefinitions ?? compileFeatures(definitions);
		for (const featureKey of Object.keys(definitions) as (keyof typeof definitions)[]) {
			const rawDefinition = definitions[featureKey];
			const compiledDefinition = compiledDefinitions[featureKey];
			const featureValueFromProp = featuresProp[featureKey];
			if (prevFeaturesProp && prevResult) {
				const prevFeatureValueFromProp = prevFeaturesProp[featureKey];
				if (featureValueFromProp === prevFeatureValueFromProp) {
					changedResult[featureKey] = prevResult[featureKey];
					continue;
				}
			}
			changed = true;

			changedResult[featureKey] = compiledDefinition.resolve(
				featureValueFromProp,
				rawDefinition as any,
			);
		}
		const result = changed || prevResult === null ? changedResult : prevResult;

		prevFeaturesProp = featuresProp;
		prevResult = result;

		return result;
	};
};

const compileFeatures = <FeatureDefs extends FeaturesDefinitionBase>(
	definitions: FeaturesDefinition<FeatureDefs>,
) => {
	const result = {} as CompiledFeaturesDefinition<FeatureDefs>;
	for (let featureKey of Object.keys(definitions) as (keyof typeof definitions)[]) {
		result[featureKey] = compileFeature(
			definitions,
			featureKey,
		) as CompiledFeaturesDefinition<FeatureDefs>[typeof featureKey];
	}
	return result;
};

const compileFeature = <
	FeatureDefs extends FeaturesDefinitionBase,
	FeatureKey extends keyof FeatureDefs,
>(
	definitions: FeaturesDefinition<FeatureDefs>,
	featureKey: FeatureKey,
) => {
	const definition = definitions[featureKey];
	if (isCustomMultivariateDescriptor(definition)) {
		return {
			resolve: customMultivariateDescriptorResolver,
		};
	} else {
		return {
			resolve: simpleDefaultValueResolver,
		};
	}
};

const isCustomMultivariateDescriptor = (
	definition: unknown,
): definition is SingleFeatureDescriptor<unknown[]> => {
	if (!definition) {
		return false;
	}
	const descriptor = definition as SingleFeatureDescriptor<unknown[]>;
	return Array.isArray(descriptor.oneOf) && descriptor.defaultValue !== undefined;
};

const simpleDefaultValueResolver = <T extends boolean | string>(
	valueFromProp: T | undefined,
	definition: T,
) => valueFromProp ?? definition;

const customMultivariateDescriptorResolver = (
	valueFromProp: string | undefined,
	definition: SingleFeatureDescriptor<string[]>,
) => valueFromProp ?? definition.defaultValue;
